<template>
  <div class="kkday-product-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">商品管理</h1>
        <div class="header-actions">
          <el-button icon="el-icon-refresh" @click="fetchProducts">刷新</el-button>
        </div>
      </div>
    </div>

    <!-- 搜索和筛选区域 -->
    <div class="filter-section">
      <el-card shadow="never" class="filter-card">
        <el-form :model="filters" :inline="true" class="filter-form">
          <el-form-item label="关键词">
            <el-input
              v-model="filters.keyword"
              placeholder="搜索商品名称、产品编号、介绍"
              clearable
              style="width: 250px"
            />
          </el-form-item>
          <el-form-item label="分类">
            <el-cascader
              v-model="filters.categoryPath"
              :options="categoryTree"
              :props="{ value: 'id', label: 'title', children: 'children', checkStrictly: true }"
              placeholder="选择分类"
              clearable
              style="width: 150px"
              @change="handleCategoryChange"
            />
          </el-form-item>
          <el-form-item label="城市">
            <el-select
              v-model="filters.city_id"
              placeholder="选择城市"
              clearable
              style="width: 150px"
            >
              <el-option
                v-for="city in cities"
                :key="city.id"
                :label="city.title"
                :value="city.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select
              v-model="filters.status"
              placeholder="选择状态"
              clearable
              style="width: 120px"
            >
              <el-option label="启用" :value="1" />
              <el-option label="禁用" :value="0" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">
              搜索
            </el-button>
            <el-button icon="el-icon-refresh" @click="resetFilters">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <!-- 商品列表 -->
    <div class="content-section">
      <el-card shadow="never" class="content-card">
        <div slot="header" class="card-header">
          <span class="card-title">商品列表 ({{ total }} 条)</span>
          <div class="header-tools">
            <!-- 视图切换 -->
            <el-radio-group v-model="viewMode" size="small" style="margin-right: 15px">
              <el-radio-button label="grid">
                <i class="el-icon-s-grid"></i> 网格
              </el-radio-button>
              <el-radio-button label="list">
                <i class="el-icon-s-list"></i> 列表
              </el-radio-button>
            </el-radio-group>
            <el-select
              v-model="filters.sort_by"
              placeholder="排序方式"
              style="width: 150px; margin-right: 10px"
              @change="handleSort"
            >
              <el-option label="创建时间" value="created_at" />
              <el-option label="更新时间" value="updated_at" />
              <el-option label="产品编号" value="prod_no" />
              <el-option label="产品名称" value="prod_name" />
              <el-option label="B2B价格" value="b2b_min_price" />
              <el-option label="B2C价格" value="b2c_min_price" />
            </el-select>
            <el-select
              v-model="filters.sort_dir"
              style="width: 100px"
              @change="handleSort"
            >
              <el-option label="降序" value="desc" />
              <el-option label="升序" value="asc" />
            </el-select>
          </div>
        </div>

        <!-- 加载状态 -->
        <div v-if="loading" class="loading-container">
          <el-skeleton :rows="5" animated />
        </div>

        <!-- 商品网格展示 -->
        <div v-else-if="products.length > 0 && viewMode === 'grid'" class="product-grid">
          <div
            v-for="product in products"
            :key="product.prod_no"
            class="product-card"
          >
            <!-- 商品图片区域 -->
            <div class="product-image">
              <img
                v-if="getProductImageUrl(product)"
                :src="getProductImageUrl(product)"
                :alt="product.prod_name"
                @error="handleImageError"
              />
              <div v-else class="no-image">
                <i class="el-icon-picture-outline"></i>
                <span>暂无图片</span>
              </div>
              <!-- 状态标签 -->
              <div class="status-tag">
                <el-tag :type="product.status === 1 ? 'success' : 'danger'" size="small">
                  {{ product.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </div>
            </div>

            <!-- 商品信息区域 -->
            <div class="product-info">
              <div class="product-header">
                <h3 class="product-name" :title="product.prod_name">
                  {{ product.prod_name }}
                </h3>
                <div class="product-id">
                  <el-tag type="info" size="mini">ID: {{ product.prod_no }}</el-tag>
                </div>
              </div>

              <!-- 分类信息 -->
              <div v-if="product.category" class="product-category">
                <i class="el-icon-collection-tag"></i>
                <span>{{ product.category.title }}</span>
              </div>

              <!-- 价格信息 -->
              <div class="product-prices">
                <div class="price-item">
                  <span class="price-label">B2B:</span>
                  <span class="price-value">{{ formatPrice(product.b2b_min_price, product.prod_currency) }}</span>
                </div>
                <div class="price-item">
                  <span class="price-label">B2C:</span>
                  <span class="price-value">{{ formatPrice(product.b2c_min_price, product.prod_currency) }}</span>
                </div>
              </div>

              <!-- 产品类型 -->
              <div class="product-type">
                <el-tag type="primary" size="mini">{{ product.prod_type || 'N/A' }}</el-tag>
                <el-tag type="info" size="mini">{{ product.prod_currency || 'N/A' }}</el-tag>
              </div>

              <!-- 商品简介 -->
              <div v-if="product.introduction" class="product-intro">
                <p v-html="stripHtml(product.introduction)"></p>
              </div>
            </div>

            <!-- 操作按钮区域 -->
            <div class="product-actions">
              <el-button
                type="text"
                icon="el-icon-view"
                @click="handleViewProduct(product)"
              >
                详情
              </el-button>
              <el-button
                type="text"
                icon="el-icon-edit"
                @click="handleEditProduct(product)"
              >
                编辑
              </el-button>
              <el-button
                type="text"
                icon="el-icon-box"
                @click="handleManagePackages(product)"
              >
                套餐
              </el-button>
              <el-button
                type="text"
                :icon="product.status === 1 ? 'el-icon-video-pause' : 'el-icon-video-play'"
                @click="handleToggleStatus(product)"
              >
                {{ product.status === 1 ? '禁用' : '启用' }}
              </el-button>
              <el-button
                type="text"
                icon="el-icon-delete"
                class="delete-btn"
                @click="handleDeleteProduct(product)"
              >
                删除
              </el-button>
            </div>
          </div>
        </div>

        <!-- 商品列表展示 -->
        <div v-else-if="products.length > 0 && viewMode === 'list'" class="product-list">
          <el-table :data="products" stripe>
            <el-table-column label="商品" min-width="280">
              <template slot-scope="scope">
                <div class="list-product-cell">
                  <img
                    v-if="getProductImageUrl(scope.row)"
                    :src="getProductImageUrl(scope.row)"
                    class="list-product-img"
                    @error="handleImageError"
                  />
                  <div v-else class="list-product-no-img">
                    <i class="el-icon-picture-outline"></i>
                  </div>
                  <div class="list-product-info">
                    <div class="list-product-name">{{ scope.row.prod_name }}</div>
                    <div class="list-product-no">ID: {{ scope.row.prod_no }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="category.title" label="分类" width="120">
              <template slot-scope="scope">
                {{ (scope.row.category && scope.row.category.title) || '-' }}
              </template>
            </el-table-column>
            <el-table-column label="B2B价格" width="110" align="right">
              <template slot-scope="scope">
                <span class="price-text">{{ formatPrice(scope.row.b2b_min_price, scope.row.prod_currency) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="B2C价格" width="110" align="right">
              <template slot-scope="scope">
                <span class="price-text">{{ formatPrice(scope.row.b2c_min_price, scope.row.prod_currency) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="prod_currency" label="货币" width="70" />
            <el-table-column prop="prod_type" label="类型" width="80">
              <template slot-scope="scope">
                <el-tag type="primary" size="mini">{{ scope.row.prod_type || 'N/A' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="80" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'" size="small">
                  {{ scope.row.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="创建时间" width="160">
              <template slot-scope="scope">
                {{ formatDateTime(scope.row.created_at) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="220" fixed="right">
              <template slot-scope="scope">
                <el-button type="text" size="small" icon="el-icon-view" @click="handleViewProduct(scope.row)">
                  详情
                </el-button>
                <el-button type="text" size="small" icon="el-icon-edit" @click="handleEditProduct(scope.row)">
                  编辑
                </el-button>
                <el-button type="text" size="small" icon="el-icon-box" @click="handleManagePackages(scope.row)">
                  套餐
                </el-button>
                <el-dropdown trigger="click" @command="(cmd) => handleDropdownCommand(cmd, scope.row)">
                  <el-button type="text" size="small">
                    更多<i class="el-icon-arrow-down el-icon--right"></i>
                  </el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="toggle" :icon="scope.row.status === 1 ? 'el-icon-video-pause' : 'el-icon-video-play'">
                      {{ scope.row.status === 1 ? '禁用' : '启用' }}
                    </el-dropdown-item>
                    <el-dropdown-item command="delete" icon="el-icon-delete" class="delete-item">删除</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 空状态 -->
        <div v-else class="empty-state">
          <i class="el-icon-box"></i>
          <p>暂无商品数据</p>
        </div>
      </el-card>
    </div>

    <!-- 分页组件 -->
    <div v-if="total > 0" class="pagination-section">
      <el-pagination
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 商品详情弹窗 -->
    <el-dialog
      title="商品详情"
      :visible.sync="detailDialogVisible"
      width="80%"
      :close-on-click-modal="false"
      class="product-detail-dialog"
    >
      <div v-if="selectedProduct" class="product-detail">
        <!-- 基础信息 -->
        <div class="detail-section">
          <h3>基础信息</h3>
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="info-item">
                <span class="label">产品编号:</span>
                <span class="value">{{ selectedProduct.prod_no }}</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="info-item">
                <span class="label">产品名称:</span>
                <span class="value">{{ selectedProduct.prod_name }}</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="info-item">
                <span class="label">状态:</span>
                <el-tag :type="selectedProduct.status === 1 ? 'success' : 'danger'">
                  {{ selectedProduct.status === 1 ? '启用' : '禁用' }}
                </el-tag>
              </div>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="info-item">
                <span class="label">产品类型:</span>
                <span class="value">{{ selectedProduct.prod_type || 'N/A' }}</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="info-item">
                <span class="label">产品货币:</span>
                <span class="value">{{ selectedProduct.prod_currency || 'N/A' }}</span>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="info-item">
                <span class="label">分类:</span>
                <span class="value">{{ (selectedProduct.category && selectedProduct.category.title) || 'N/A' }}</span>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- 价格信息 -->
        <div class="detail-section">
          <h3>价格信息</h3>
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="info-item">
                <span class="label">B2B最低价格:</span>
                <span class="value price-highlight">
                  {{ formatPrice(selectedProduct.b2b_min_price, selectedProduct.prod_currency) }}
                </span>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="info-item">
                <span class="label">B2C最低价格:</span>
                <span class="value price-highlight">
                  {{ formatPrice(selectedProduct.b2c_min_price, selectedProduct.prod_currency) }}
                </span>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- 产品介绍 -->
        <div v-if="selectedProduct.introduction" class="detail-section">
          <h3>产品介绍</h3>
          <div class="introduction-content" v-html="stripHtml(selectedProduct.introduction)">
          </div>
        </div>

        <!-- 产品图片 -->
        <div v-if="getDetailProductImages().length > 0" class="detail-section">
          <h3>产品图片 ({{ getDetailProductImages().length }}张)</h3>
          <div class="banners-grid">
            <div
              v-for="(image, index) in getDetailProductImages()"
              :key="'img-' + index"
              class="banner-item"
            >
              <el-image
                :src="image"
                :alt="'产品图片' + (index + 1)"
                fit="cover"
                :preview-src-list="getDetailProductImages()"
                class="banner-image"
              />
            </div>
          </div>
        </div>

        <!-- 产品视频 -->
        <div v-if="selectedProduct.video_list && selectedProduct.video_list.length > 0" class="detail-section">
          <h3>产品视频 ({{ selectedProduct.video_list.length }}个)</h3>
          <div class="videos-grid">
            <div
              v-for="(video, index) in selectedProduct.video_list"
              :key="'video-' + index"
              class="video-item"
            >
              <video
                :src="video"
                controls
                class="detail-video"
                preload="metadata"
              >
                您的浏览器不支持视频播放
              </video>
              <div class="video-label">视频 {{ index + 1 }}</div>
            </div>
          </div>
        </div>

        <!-- 时间信息 -->
        <div class="detail-section">
          <h3>时间信息</h3>
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="info-item">
                <span class="label">创建时间:</span>
                <span class="value">{{ formatDateTime(selectedProduct.created_at) }}</span>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="info-item">
                <span class="label">更新时间:</span>
                <span class="value">{{ formatDateTime(selectedProduct.updated_at) }}</span>
              </div>
            </el-col>
          </el-row>
        </div>
      </div>
    </el-dialog>

    <!-- 商品编辑弹窗 -->
    <el-dialog
      title="编辑商品"
      :visible.sync="editDialogVisible"
      width="80%"
      :close-on-click-modal="false"
      class="product-edit-dialog"
    >
      <el-form
        ref="productForm"
        :model="productForm"
        :rules="productRules"
        label-width="120px"
      >
        <!-- 基础信息 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="产品编号">
              <el-input
                v-model="productForm.prod_no"
                placeholder="产品编号"
                :disabled="true"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="产品URL编号">
              <el-input
                v-model="productForm.prod_url_no"
                placeholder="产品URL编号"
                disabled
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="产品名称" prop="prod_name">
          <el-input
            v-model="productForm.prod_name"
            placeholder="请输入产品名称"
          />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="产品货币">
              <el-select
                v-model="productForm.prod_currency"
                placeholder="请选择货币类型"
                style="width: 100%"
              >
                <el-option label="TWD (台币)" value="TWD" />
                <el-option label="JPY (日元)" value="JPY" />
                <el-option label="USD (美元)" value="USD" />
                <el-option label="CNY (人民币)" value="CNY" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="城市">
              <el-select
                v-model="productForm.city_id"
                placeholder="请选择城市"
                clearable
                style="width: 100%"
              >
                <el-option
                  v-for="city in cities"
                  :key="city.id"
                  :label="city.title"
                  :value="city.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="分类">
              <el-cascader
                v-model="productForm.categoryPath"
                :options="categoryTree"
                :props="{ value: 'id', label: 'title', children: 'children', checkStrictly: true }"
                placeholder="请选择分类"
                clearable
                style="width: 100%"
                @change="handleProductCategoryChange"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 价格信息 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="B2B最低价格">
              <el-input-number
                v-model="productForm.b2b_min_price"
                :precision="2"
                :min="0"
                :max="999999"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="B2C最低价格">
              <el-input-number
                v-model="productForm.b2c_min_price"
                :precision="2"
                :min="0"
                :max="999999"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 时间信息 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="线上开始日期">
              <el-date-picker
                v-model="productForm.online_s_date"
                type="date"
                placeholder="选择开始日期"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="线上结束日期">
              <el-date-picker
                v-model="productForm.online_e_date"
                type="date"
                placeholder="选择结束日期"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 产品介绍 - 简要介绍 -->
        <el-form-item label="产品介绍">
          <el-input
            v-model="productForm.introduction"
            type="textarea"
            :rows="3"
            placeholder="请输入产品简要介绍"
          />
        </el-form-item>

        <!-- 产品图片上传 -->
        <el-form-item label="产品图片" class="product-image-form-item">
          <el-upload
            class="image-uploader"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :show-file-list="false"
            :on-success="handleProductImageSuccess"
            :on-error="handleUploadError"
            :before-upload="beforeImageUpload"
          >
            <img v-if="productForm.prod_img_url" :src="productForm.prod_img_url" class="product-image-upload" />
            <i v-else class="el-icon-plus image-uploader-icon"></i>
          </el-upload>
          <div class="upload-tip">支持 jpg、png 格式，文件大小不超过 5MB</div>
        </el-form-item>

        <!-- 图片列表上传 -->
        <el-form-item label="图片列表">
          <div class="media-upload-container">
            <div class="media-grid">
              <div
                v-for="(image, index) in productForm.img_list"
                :key="'img-' + index"
                class="media-item"
              >
                <img :src="image" class="media-preview" />
                <div class="media-actions">
                  <i class="el-icon-delete" @click="removeImage(index)"></i>
                </div>
                <div class="media-label">图片 {{ index + 1 }}</div>
              </div>
            </div>
            <el-upload
              class="media-uploader"
              :action="uploadUrl"
              :headers="uploadHeaders"
              :show-file-list="false"
              :on-success="handleImageSuccess"
              :on-error="handleUploadError"
              :before-upload="beforeImageUpload"
            >
              <div class="upload-placeholder">
                <i class="el-icon-plus"></i>
                <span>上传图片</span>
              </div>
            </el-upload>
          </div>
          <div class="upload-tip">支持多张图片，jpg、png 格式，文件大小不超过 5MB</div>
        </el-form-item>

        <!-- 视频列表上传 -->
        <el-form-item label="视频列表">
          <div class="media-upload-container">
            <div class="media-grid video-grid">
              <div
                v-for="(video, index) in productForm.video_list"
                :key="'video-' + index"
                class="media-item"
              >
                <video :src="video" class="media-preview video-preview" controls></video>
                <div class="media-actions">
                  <i class="el-icon-delete" @click="removeVideo(index)"></i>
                </div>
                <div class="media-label">视频 {{ index + 1 }}</div>
              </div>
            </div>
            <el-upload
              class="media-uploader video-uploader"
              :action="uploadUrl"
              :headers="uploadHeaders"
              :show-file-list="false"
              :on-success="handleVideoSuccess"
              :on-error="handleUploadError"
              :before-upload="beforeVideoUpload"
            >
              <div class="upload-placeholder">
                <i class="el-icon-video-play"></i>
                <span>上传视频</span>
              </div>
            </el-upload>
          </div>
          <div class="upload-tip">支持视频文件，mp4、avi 等格式，文件大小不超过 50MB</div>
        </el-form-item>

        <!-- 设置选项 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="免费取消">
              <el-radio-group v-model="productForm.is_cancel_free">
                <el-radio :label="1">是</el-radio>
                <el-radio :label="0">否</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="状态">
              <el-radio-group v-model="productForm.status">
                <el-radio :label="1">启用</el-radio>
                <el-radio :label="0">禁用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 详细内容 - 富文本编辑器 -->
        <el-form-item label="详细内容" class="rich-text-form-item">
          <div class="quill-editor-wrapper">
            <quill-editor
              v-model="productForm.content"
              ref="myQuillEditor"
              :options="editorOption"
            />
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveProduct">
          更新
        </el-button>
      </div>
    </el-dialog>

    <!-- 套餐管理弹窗 -->
    <el-dialog
      title="套餐管理"
      :visible.sync="packagesDialogVisible"
      width="90%"
      :close-on-click-modal="false"
      class="packages-dialog"
    >
      <div v-if="currentProduct" class="packages-content">
        <div class="packages-header">
          <h3>{{ currentProduct.prod_name }} - 套餐列表</h3>
          <el-button
            icon="el-icon-refresh"
            @click="fetchPackages(currentProduct.prod_no)"
          >
            刷新套餐
          </el-button>
        </div>
        <el-table v-loading="packagesLoading" :data="packages" stripe>
          <el-table-column prop="pkg_no" label="套餐编号" width="100" />
          <el-table-column prop="pkg_name" label="套餐名称" min-width="200" />
          <el-table-column prop="exchange_type" label="兑换类型" width="100" />
          <el-table-column prop="confirm_time" label="确认时间" width="120">
            <template slot-scope="scope">
              {{ scope.row.confirm_time }}{{ scope.row.confirm_time_unit }}
            </template>
          </el-table-column>
          <el-table-column prop="b2b_min_price" label="B2B价格" width="100">
            <template slot-scope="scope">
              {{ formatPrice(scope.row.b2b_min_price, currentProduct.prod_currency) }}
            </template>
          </el-table-column>
          <el-table-column prop="b2c_min_price" label="B2C价格" width="100">
            <template slot-scope="scope">
              {{ formatPrice(scope.row.b2c_min_price, currentProduct.prod_currency) }}
            </template>
          </el-table-column>
          <el-table-column prop="sale_s_date" label="销售开始日期" width="130" />
          <el-table-column prop="sale_e_date" label="销售结束日期" width="130" />
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <el-button
                type="text"
                size="small"
                @click="handleEditPackage(currentProduct.prod_no, scope.row)"
              >
                编辑
              </el-button>
              <el-button
                type="text"
                size="small"
                class="delete-btn"
                @click="handleDeletePackage(currentProduct.prod_no, scope.row)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        <div v-if="packages.length === 0 && !packagesLoading" class="empty-packages">
          <i class="el-icon-box"></i>
          <p>该商品暂无套餐</p>
        </div>
      </div>
    </el-dialog>

    <!-- 套餐编辑弹窗 -->
    <el-dialog
      title="编辑套餐"
      :visible.sync="packageEditDialogVisible"
      width="80%"
      :close-on-click-modal="false"
      class="package-edit-dialog"
    >
      <el-form
        v-if="packageForm"
        ref="packageForm"
        :model="packageForm"
        :rules="packageRules"
        label-width="120px"
        label-position="left"
      >
        <!-- 基本信息 -->
        <div class="form-section">
          <h4 class="section-title">基本信息</h4>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="套餐名称" prop="pkg_name">
                <el-input v-model="packageForm.pkg_name" placeholder="请输入套餐名称" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="套餐编号" prop="pkg_no">
                <el-input v-model="packageForm.pkg_no" placeholder="请输入套餐编号" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="兑换类型" prop="exchange_type">
                <el-select v-model="packageForm.exchange_type" placeholder="请选择兑换类型" style="width: 100%">
                  <el-option label="电子兑换" value="electronic" />
                  <el-option label="纸质兑换" value="paper" />
                  <el-option label="实体兑换" value="physical" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="确认时间" prop="confirm_time">
                <el-row :gutter="10">
                  <el-col :span="16">
                    <el-input-number
                      v-model="packageForm.confirm_time"
                      :min="0"
                      :max="999"
                      style="width: 100%"
                    />
                  </el-col>
                  <el-col :span="8">
                    <el-select v-model="packageForm.confirm_time_unit" style="width: 100%">
                      <el-option label="分钟" value="分钟" />
                      <el-option label="小时" value="小时" />
                      <el-option label="天" value="天" />
                    </el-select>
                  </el-col>
                </el-row>
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- 价格信息 -->
        <div class="form-section">
          <h4 class="section-title">价格信息</h4>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="B2B最低价格" prop="b2b_min_price">
                <el-input-number
                  v-model="packageForm.b2b_min_price"
                  :precision="2"
                  :min="0"
                  :max="999999"
                  style="width: 100%"
                />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="B2C最低价格" prop="b2c_min_price">
                <el-input-number
                  v-model="packageForm.b2c_min_price"
                  :precision="2"
                  :min="0"
                  :max="999999"
                  style="width: 100%"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- 销售日期 -->
        <div class="form-section">
          <h4 class="section-title">销售日期</h4>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="销售开始日期" prop="sale_s_date">
                <el-date-picker
                  v-model="packageForm.sale_s_date"
                  type="date"
                  placeholder="选择开始日期"
                  style="width: 100%"
                />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="销售结束日期" prop="sale_e_date">
                <el-date-picker
                  v-model="packageForm.sale_e_date"
                  type="date"
                  placeholder="选择结束日期"
                  style="width: 100%"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <!-- 使用说明 -->
        <div class="form-section">
          <h4 class="section-title">使用说明</h4>
          <el-form-item label="使用说明" prop="usage_instructions">
            <el-input
              v-model="packageForm.usage_instructions"
              type="textarea"
              :rows="4"
              placeholder="请输入使用说明"
            />
          </el-form-item>
        </div>

        <!-- 包含内容 -->
        <div class="form-section">
          <h4 class="section-title">包含内容</h4>
          <el-form-item label="包含内容" prop="inclusions">
            <el-input
              v-model="packageForm.inclusions"
              type="textarea"
              :rows="4"
              placeholder="请输入套餐包含的内容"
            />
          </el-form-item>
        </div>

        <!-- 注意事项 -->
        <div class="form-section">
          <h4 class="section-title">注意事项</h4>
          <el-form-item label="注意事项" prop="important_notes">
            <el-input
              v-model="packageForm.important_notes"
              type="textarea"
              :rows="4"
              placeholder="请输入注意事项"
            />
          </el-form-item>
        </div>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="packageEditDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="packageSubmitting" @click="handleSavePackage">
          保存
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { kkdayProductApi } from '@/api'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import { quillEditor } from 'vue-quill-editor'

export default {
  name: 'KkdayProductManagement',
  components: {
    quillEditor
  },
  data() {
    return {
      // 视图模式: grid 网格, list 列表
      viewMode: 'list',

      // 列表相关数据
      products: [],
      loading: false,
      total: 0,
      currentPage: 1,
      pageSize: 20,

      // 筛选条件
      filters: {
        keyword: '',
        categoryPath: [],
        cate_id: null,
        cate_sub_id: null,
        city_id: null,
        status: null,
        sort_by: 'created_at',
        sort_dir: 'desc'
      },

      // 分类数据
      categories: [],
      categoryTree: [],
      // 城市数据
      cities: [],

      // 详情弹窗
      detailDialogVisible: false,
      selectedProduct: null,

      // 编辑弹窗
      editDialogVisible: false,
      isEditMode: false,
      saving: false,
      productForm: {
        prod_no: null,
        prod_url_no: null,
        prod_name: '',
        prod_type: '',
        prod_currency: '',
        cate_id: null,
        cate_sub_id: null,
        categoryPath: [],
        b2b_min_price: 0,
        b2c_min_price: 0,
        introduction: '',
        content: '',
        prod_img_url: '',
        img_list: [],
        video_list: [],
        status: 1,
        is_tour: 1,
        timezone: '',
        instant_confirm: 1,
        confirm_order_time: 1,
        is_cancel_free: 1,
        tourism_product_type: '',
        online_s_date: '',
        online_e_date: '',
        days: 0,
        hours: 0,
        minute: 0,
        instant_booking: 1,
        order_count: 0,
        is_all_sold_out: 0,
        city_id: null
      },
      productRules: {
        prod_name: [
          { required: true, message: '请输入产品名称', trigger: 'blur' },
          { max: 255, message: '产品名称不能超过255个字符', trigger: 'blur' }
        ]
      },

      // 套餐管理
      packagesDialogVisible: false,
      packages: [],
      packagesLoading: false,
      currentProduct: null,

      // 套餐编辑
      packageEditDialogVisible: false,
      packageForm: null,
      packageSubmitting: false,
      packageRules: {
        pkg_name: [
          { required: true, message: '请输入套餐名称', trigger: 'blur' },
          { max: 255, message: '套餐名称不能超过255个字符', trigger: 'blur' }
        ],
        pkg_no: [
          { required: true, message: '请输入套餐编号', trigger: 'blur' },
          { max: 100, message: '套餐编号不能超过100个字符', trigger: 'blur' }
        ],
        exchange_type: [
          { required: true, message: '请选择兑换类型', trigger: 'change' }
        ],
        b2b_min_price: [
          { required: true, message: '请输入B2B最低价格', trigger: 'blur' }
        ],
        b2c_min_price: [
          { required: true, message: '请输入B2C最低价格', trigger: 'blur' }
        ]
      },

      // 富文本编辑器配置
      editorOption: {
        theme: 'snow',
        placeholder: '请输入详细内容...',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean'],
            ['link', 'image', 'video']
          ]
        }
      },
      uploadUrl: process.env.VUE_APP_API_BASE_URL + '/upload/image', // 图片上传接口
      uploadHeaders: {
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      }
    }
  },
  created() {
    this.fetchProducts()
    this.fetchCategories()
    this.fetchCities()
  },
  methods: {
    // 获取商品列表
    async fetchProducts() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          keyword: this.filters.keyword,
          cate_id: this.filters.cate_id,
          cate_sub_id: this.filters.cate_sub_id,
          city_id: this.filters.city_id,
          status: this.filters.status,
          sort_by: this.filters.sort_by,
          sort_dir: this.filters.sort_dir
        }

        // 移除空值参数
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key]
          }
        })

        const response = await kkdayProductApi.getList(params)
        if (response.code === 1) {
          this.products = response.data.list
          this.total = response.data.total
          this.currentPage = response.data.page
          this.pageSize = response.data.pageSize
        } else {
          this.$message.error(response.message || '获取商品列表失败')
        }
      } catch (error) {
        console.error('获取商品列表失败:', error)
        this.$message.error('获取商品列表失败')
      } finally {
        this.loading = false
      }
    },

    // 获取分类列表
    async fetchCategories() {
      try {
        const response = await kkdayProductApi.getCategoryList()
        if (response.code === 1) {
          this.categories = response.data.list || []
          this.categoryTree = this.buildCategoryTree(this.categories)
        }
      } catch (error) {
        console.error('获取分类列表失败:', error)
      }
    },

    // 获取城市列表
    async fetchCities() {
      try {
        const response = await kkdayProductApi.getCityList()
        if (response.code === 1) {
          this.cities = response.data.list || []
        }
      } catch (error) {
        console.error('获取城市列表失败:', error)
      }
    },

    // 构建分类树（用于级联选择器）
    buildCategoryTree(list) {
      return list.map(item => ({
        ...item,
        children: item.children && item.children.length > 0 ? item.children : undefined
      }))
    },

    // 处理分类选择变化
    handleCategoryChange(value) {
      if (value && value.length > 0) {
        this.filters.cate_id = value[0]
        this.filters.cate_sub_id = value.length > 1 ? value[1] : null
      } else {
        this.filters.cate_id = null
        this.filters.cate_sub_id = null
      }
    },

    // 搜索
    handleSearch() {
      this.currentPage = 1
      this.fetchProducts()
    },

    // 重置筛选
    resetFilters() {
      this.filters = {
        keyword: '',
        categoryPath: [],
        cate_id: null,
        cate_sub_id: null,
        city_id: null,
        status: null,
        sort_by: 'created_at',
        sort_dir: 'desc'
      }
      this.currentPage = 1
      this.fetchProducts()
    },

    // 排序
    handleSort() {
      this.currentPage = 1
      this.fetchProducts()
    },

    // 处理下拉菜单命令
    handleDropdownCommand(command, product) {
      if (command === 'toggle') {
        this.handleToggleStatus(product)
      } else if (command === 'delete') {
        this.handleDeleteProduct(product)
      }
    },

    // 分页相关
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.fetchProducts()
    },

    handleCurrentChange(val) {
      this.currentPage = val
      this.fetchProducts()
    },

    // 查看商品详情
    async handleViewProduct(product) {
      try {
        const response = await kkdayProductApi.getDetail(product.prod_no)
        if (response.code === 1) {
          this.selectedProduct = response.data.info
          this.detailDialogVisible = true
        } else {
          this.$message.error(response.message || '获取商品详情失败')
        }
      } catch (error) {
        console.error('获取商品详情失败:', error)
        this.$message.error('获取商品详情失败')
      }
    },

  
    // 编辑商品
    async handleEditProduct(product) {
      try {
        // 先获取完整商品详情
        const response = await kkdayProductApi.getDetail(product.prod_no)
        if (response.code === 1) {
          const detailData = response.data.info
          this.isEditMode = true

          // 构建分类路径
          const categoryPath = this.buildCategoryPath(detailData.cate_id, detailData.cate_sub_id)

          this.productForm = {
            prod_no: detailData.prod_no,
            prod_name: detailData.prod_name,
            prod_type: detailData.prod_type || '',
            prod_currency: detailData.prod_currency || '',
            cate_id: detailData.cate_id,
            cate_sub_id: detailData.cate_sub_id,
            categoryPath: categoryPath,
            b2b_min_price: parseFloat(detailData.b2b_min_price) || 0,
            b2c_min_price: parseFloat(detailData.b2c_min_price) || 0,
            introduction: detailData.introduction || '',
            content: detailData.content || '',
            img_list: detailData.img_list || [],
            video_list: detailData.video_list || [],
            status: detailData.status,
            prod_url_no: detailData.prod_url_no,
            is_tour: detailData.is_tour || 0,
            timezone: detailData.timezone || '',
            instant_confirm: detailData.instant_confirm || 1,
            confirm_order_time: detailData.confirm_order_time || 1,
            is_cancel_free: detailData.is_cancel_free || 1,
            tourism_product_type: detailData.tourism_product_type || '',
            online_s_date: detailData.online_s_date || '',
            online_e_date: detailData.online_e_date || '',
            days: detailData.days || 0,
            hours: detailData.hours || 0,
            minute: detailData.minute || 0,
            instant_booking: detailData.instant_booking || 1,
            order_count: detailData.order_count || 0,
            is_all_sold_out: detailData.is_all_sold_out || 0,
            city_id: detailData.city_id || null
          }
          this.editDialogVisible = true
        } else {
          this.$message.error(response.message || '获取商品详情失败')
        }
      } catch (error) {
        console.error('获取商品详情失败:', error)
        this.$message.error('获取商品详情失败')
      }
    },

    // 根据 cate_id 和 cate_sub_id 构建分类路径
    buildCategoryPath(cateId, cateSubId) {
      const path = []
      if (cateId) {
        path.push(cateId)
      }
      if (cateSubId) {
        path.push(cateSubId)
      }
      return path
    },

    // 处理编辑表单的分类变化
    handleProductCategoryChange(value) {
      if (value && value.length > 0) {
        this.productForm.cate_id = value[0]
        this.productForm.cate_sub_id = value.length > 1 ? value[1] : null
      } else {
        this.productForm.cate_id = null
        this.productForm.cate_sub_id = null
      }
    },

    // 保存商品
    async saveProduct() {
      try {
        await this.$refs.productForm.validate()

        // img_list 和 video_list 已经通过上传组件处理

        // 处理日期格式
        if (this.productForm.online_s_date) {
          this.productForm.online_s_date = new Date(this.productForm.online_s_date).toISOString().split('T')[0]
        }
        if (this.productForm.online_e_date) {
          this.productForm.online_e_date = new Date(this.productForm.online_e_date).toISOString().split('T')[0]
        }

        // 构建提交数据，排除 categoryPath（仅用于前端显示）
        const submitData = { ...this.productForm }
        delete submitData.categoryPath

        this.saving = true
        const response = await kkdayProductApi.edit(submitData)

        if (response.code === 1) {
          this.$message.success('更新成功')
          this.editDialogVisible = false
          this.fetchProducts()
        } else {
          this.$message.error(response.message || '更新失败')
        }
      } catch (error) {
        if (error !== 'validation failed') {
          console.error('保存商品失败:', error)
          this.$message.error('保存商品失败')
        }
      } finally {
        this.saving = false
      }
    },

    
    // 切换状态
    async handleToggleStatus(product) {
      try {
        const action = product.status === 1 ? '禁用' : '启用'
        await this.$confirm(`确定要${action}商品"${product.prod_name}"吗？`, '确认操作', {
          type: 'warning'
        })

        const response = await kkdayProductApi.edit({
          prod_no: product.prod_no,
          status: product.status === 1 ? 0 : 1
        })

        if (response.code === 1) {
          this.$message.success(`${action}成功`)
          this.fetchProducts()
        } else {
          this.$message.error(response.message || `${action}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('切换状态失败:', error)
          this.$message.error('操作失败')
        }
      }
    },

    // 删除商品
    async handleDeleteProduct(product) {
      try {
        await this.$confirm(`确定要删除商品"${product.prod_name}"吗？`, '确认删除', {
          type: 'warning'
        })

        const response = await kkdayProductApi.delete(product.prod_no)
        if (response.code === 1) {
          this.$message.success('删除成功')
          this.fetchProducts()
        } else {
          this.$message.error(response.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除商品失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    // 管理套餐
    async handleManagePackages(product) {
      this.currentProduct = product
      this.packagesDialogVisible = true
      await this.fetchPackages(product.prod_no)
    },

    // 获取套餐列表
    async fetchPackages(prodNo) {
      this.packagesLoading = true
      try {
        const response = await kkdayProductApi.getPackagesList(prodNo)
        if (response.code === 1) {
          this.packages = response.data.list || []
        } else {
          this.$message.error(response.message || '获取套餐列表失败')
        }
      } catch (error) {
        console.error('获取套餐列表失败:', error)
        this.$message.error('获取套餐列表失败')
      } finally {
        this.packagesLoading = false
      }
    },

    // 编辑套餐
    async handleEditPackage(prodNo, pkg) {
      try {
        // 获取套餐详情
        const response = await kkdayProductApi.getPackageDetail(prodNo, pkg.id)
        if (response.code === 1) {
          const detailData = response.data.info
          this.packageForm = {
            id: detailData.id,
            prod_no: detailData.prod_no,
            pkg_name: detailData.pkg_name || '',
            pkg_no: detailData.pkg_no || '',
            exchange_type: detailData.exchange_type || 'electronic',
            confirm_time: parseInt(detailData.confirm_time) || 0,
            confirm_time_unit: detailData.confirm_time_unit || '分钟',
            b2b_min_price: parseFloat(detailData.b2b_min_price) || 0,
            b2c_min_price: parseFloat(detailData.b2c_min_price) || 0,
            sale_s_date: detailData.sale_s_date || '',
            sale_e_date: detailData.sale_e_date || '',
            usage_instructions: detailData.usage_instructions || '',
            inclusions: detailData.inclusions || '',
            important_notes: detailData.important_notes || ''
          }
          this.packageEditDialogVisible = true
        } else {
          this.$message.error(response.message || '获取套餐详情失败')
        }
      } catch (error) {
        console.error('获取套餐详情失败:', error)
        this.$message.error('获取套餐详情失败')
      }
    },

    // 保存套餐
    async handleSavePackage() {
      try {
        await this.$refs.packageForm.validate()
        this.packageSubmitting = true

        const formData = { ...this.packageForm }

        // 格式化日期
        if (formData.sale_s_date) {
          formData.sale_s_date = this.formatDate(formData.sale_s_date)
        }
        if (formData.sale_e_date) {
          formData.sale_e_date = this.formatDate(formData.sale_e_date)
        }

        const response = await kkdayProductApi.editPackage(formData.prod_no, formData.id, formData)
        if (response.code === 1) {
          this.$message.success('套餐保存成功')
          this.packageEditDialogVisible = false
          // 刷新套餐列表
          await this.fetchPackages(formData.prod_no)
        } else {
          this.$message.error(response.message || '套餐保存失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('保存套餐失败:', error)
          this.$message.error('保存套餐失败')
        }
      } finally {
        this.packageSubmitting = false
      }
    },

    // 删除套餐
    async handleDeletePackage(prodNo, pkg) {
      try {
        await this.$confirm(`确定要删除套餐"${pkg.pkg_name}"吗？`, '确认删除', {
          type: 'warning'
        })

        const response = await kkdayProductApi.deletePackage(prodNo, pkg.id)
        if (response.code === 1) {
          this.$message.success('删除成功')
          this.fetchPackages(prodNo)
        } else {
          this.$message.error(response.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除套餐失败:', error)
          this.$message.error('删除失败')
        }
      }
    },

    // 处理图片错误
    handleImageError(event) {
      const parentElement = event.target.parentElement
      if (parentElement) {
        parentElement.innerHTML = `
          <div class="no-image">
            <i class="el-icon-picture-outline"></i>
            <span>图片加载失败</span>
          </div>
        `
      }
    },

    // 工具方法
    formatPrice(price, currency = 'TWD') {
      if (!price) return 'N/A'
      const num = parseFloat(price)
      if (isNaN(num)) return 'N/A'

      const currencySymbols = {
        TWD: 'NT$',
        JPY: '¥',
        USD: '$',
        CNY: '¥'
      }

      const symbol = currencySymbols[currency] || currency
      return `${symbol}${num.toLocaleString()}`
    },

    formatDateTime(dateStr) {
      if (!dateStr) return 'N/A'
      try {
        return new Date(dateStr).toLocaleString('zh-CN')
      } catch {
        return dateStr
      }
    },

    // 去除HTML标签
    stripHtml(html) {
      if (!html) return ''
      return html.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ')
    },

    // 获取商品图片URL
    getProductImageUrl(product) {
      // 优先使用prod_img_url，然后使用img_list数组的第一个元素
      if (product.prod_img_url) {
        return product.prod_img_url
      }
      if (product.img_list && product.img_list.length > 0 && product.img_list[0]) {
        return product.img_list[0]
      }
      return null
    },

    // 获取详情页面的所有图片
    getDetailProductImages() {
      if (!this.selectedProduct) return []
      const images = []

      // 先添加prod_img_url
      if (this.selectedProduct.prod_img_url) {
        images.push(this.selectedProduct.prod_img_url)
      }

      // 再添加img_list数组中的图片
      if (this.selectedProduct.img_list && Array.isArray(this.selectedProduct.img_list)) {
        this.selectedProduct.img_list.forEach(img => {
          if (img && !images.includes(img)) {
            images.push(img)
          }
        })
      }

      return images
    },

    
    // 图片上传相关方法
    beforeImageUpload(file) {
      const isJPG = file.type === 'image/jpeg'
      const isPNG = file.type === 'image/png'
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isJPG && !isPNG) {
        this.$message.error('只能上传 JPG/PNG 格式的图片!')
        return false
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB!')
        return false
      }
      return true
    },

    handleProductImageSuccess(response) {
      if (response.code === 1) {
        this.productForm.prod_img_url = response.data.url
        this.$message.success('产品图片上传成功')
      } else {
        this.$message.error(response.message || '图片上传失败')
      }
    },

    handleImageSuccess(response) {
      if (response.code === 1) {
        this.productForm.img_list.push(response.data.url)
        this.$message.success('图片上传成功')
      } else {
        this.$message.error(response.message || '图片上传失败')
      }
    },

    handleVideoSuccess(response) {
      if (response.code === 1) {
        this.productForm.video_list.push(response.data.url)
        this.$message.success('视频上传成功')
      } else {
        this.$message.error(response.message || '视频上传失败')
      }
    },

    handleUploadError() {
      this.$message.error('上传失败，请重试')
    },

    removeImage(index) {
      this.productForm.img_list.splice(index, 1)
    },

    removeVideo(index) {
      this.productForm.video_list.splice(index, 1)
    },

    beforeVideoUpload(file) {
      const isVideo = file.type.startsWith('video/')
      const isLt50M = file.size / 1024 / 1024 < 50

      if (!isVideo) {
        this.$message.error('只能上传视频文件!')
        return false
      }
      if (!isLt50M) {
        this.$message.error('视频大小不能超过 50MB!')
        return false
      }
      return true
    }
  }
}
</script>

<style lang="scss" scoped>
.kkday-product-management {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

// 页面头部
.page-header {
  margin-bottom: 20px;

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: white;
    padding: 20px 24px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #303133;
    margin: 0;
  }

  .header-actions {
    .el-button {
      margin-left: 12px;
    }
  }
}

// 筛选区域
.filter-section {
  margin-bottom: 20px;

  .filter-card {
    border-radius: 8px;

    ::v-deep .el-card__body {
      padding: 20px 24px;
    }
  }

  .filter-form {
    margin: 0;

    .el-form-item {
      margin-bottom: 0;
      margin-right: 20px;
    }
  }
}

// 内容区域
.content-section {
  margin-bottom: 20px;

  .content-card {
    border-radius: 8px;
    min-height: 400px;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .card-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }

    .header-tools {
      display: flex;
      align-items: center;
    }
  }

  .loading-container {
    padding: 20px;
  }
}

// 商品网格
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  padding: 20px;
}

.product-card {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
}

// 商品图片
.product-image {
  position: relative;
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .no-image {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: #f5f7fa;
    color: #909399;

    i {
      font-size: 40px;
      margin-bottom: 8px;
    }

    span {
      font-size: 14px;
    }
  }

  .status-tag {
    position: absolute;
    top: 12px;
    right: 12px;
  }
}

// 商品信息
.product-info {
  padding: 16px;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;

  .product-name {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin: 0;
    flex: 1;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .product-id {
    margin-left: 8px;
  }
}

.product-category {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  color: #606266;
  font-size: 14px;

  i {
    margin-right: 4px;
    color: #409eff;
  }
}

.product-prices {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;

  .price-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;

    .price-label {
      font-size: 12px;
      color: #909399;
      margin-bottom: 4px;
    }

    .price-value {
      font-size: 16px;
      font-weight: 600;
      color: #e6a23c;
    }
  }
}

.product-type {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.product-intro {
  p {
    font-size: 14px;
    color: #606266;
    line-height: 1.5;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}

// 操作按钮
.product-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #f0f2f5;
  background: #fafafa;

  .el-button {
    padding: 4px 8px;
    font-size: 12px;

    &.delete-btn {
      color: #f56c6c;

      &:hover {
        color: #f78989;
      }
    }
  }
}

// 空状态
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #909399;

  i {
    font-size: 60px;
    margin-bottom: 16px;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

// 商品列表视图
.product-list {
  padding: 0 20px 20px;

  .list-product-cell {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .list-product-img {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .list-product-no-img {
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f7fa;
    border-radius: 4px;
    color: #c0c4cc;
    flex-shrink: 0;

    i {
      font-size: 24px;
    }
  }

  .list-product-info {
    flex: 1;
    min-width: 0;
  }

  .list-product-name {
    font-size: 14px;
    font-weight: 500;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .list-product-no {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
  }

  .price-text {
    font-weight: 600;
    color: #e6a23c;
  }

  ::v-deep .el-dropdown-menu__item.delete-item {
    color: #f56c6c;

    &:hover {
      background-color: #fef0f0;
      color: #f56c6c;
    }
  }
}

// 分页
.pagination-section {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

// 详情弹窗样式
.product-detail-dialog {
  ::v-deep .el-dialog__body {
    padding: 20px 24px;
    max-height: 70vh;
    overflow-y: auto;
  }
}

.product-detail {
  .detail-section {
    margin-bottom: 24px;

    h3 {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid #e4e7ed;
    }
  }

  .info-item {
    display: flex;
    align-items: center;
    margin-bottom: 12px;

    .label {
      font-weight: 500;
      color: #606266;
      margin-right: 12px;
      min-width: 80px;
    }

    .value {
      color: #303133;

      &.price-highlight {
        font-size: 16px;
        font-weight: 600;
        color: #e6a23c;
      }
    }
  }

  .introduction-content {
    padding: 16px;
    background: #f8f9fa;
    border-radius: 4px;
    line-height: 1.6;
    color: #606266;
    white-space: pre-wrap;
  }

  .banners-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;

    .banner-item {
      .banner-image {
        width: 100%;
        height: 150px;
        border-radius: 4px;
        cursor: pointer;
      }
    }
  }

  .videos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
  }

  .video-item {
    position: relative;
    border-radius: 4px;
    overflow: hidden;
    border: 1px solid #e4e7ed;
  }

  .detail-video {
    width: 100%;
    height: 200px;
    background: #000;
  }

  .video-label {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 8px;
    font-size: 12px;
    text-align: center;
  }
}

// 编辑弹窗样式
.product-edit-dialog {
  ::v-deep .el-dialog__body {
    padding: 20px 24px;
    max-height: 80vh;
    overflow-y: auto;
  }

  ::v-deep .el-row {
    margin-bottom: 0;
  }

  ::v-deep .el-form-item {
    margin-bottom: 18px;

    // 富文本编辑器后的表单项需要额外间距，避免被图片上传UI遮挡
    &:has(.quill-editor) {
      margin-bottom: 24px;
    }

    // 图片上传表单项也需要额外间距
    &:has(.image-uploader) {
      margin-top: 20px;
    }
  }

  // 富文本编辑器表单项特殊样式
  .rich-text-form-item {
    margin-bottom: 40px !important;
    position: relative;
    z-index: 100;

    ::v-deep .el-form-item__content {
      position: relative;
      z-index: 100;
    }
  }

  // 产品图片表单项样式
  .product-image-form-item {
    position: relative;
    z-index: 1;
    margin-top: 10px;
  }
}

// 套餐管理弹窗
.packages-dialog {
  ::v-deep .el-dialog__body {
    padding: 0 24px 20px;
  }
}

.packages-content {
  .packages-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 20px 0;
    border-bottom: 1px solid #e4e7ed;

    h3 {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      margin: 0;
    }
  }

  .empty-packages {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    color: #909399;

    i {
      font-size: 40px;
      margin-bottom: 12px;
    }

    p {
      margin: 0;
      font-size: 14px;
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .product-list {
    padding: 10px;

    ::v-deep .el-table {
      font-size: 12px;

      .list-product-img,
      .list-product-no-img {
        width: 40px;
        height: 40px;
      }

      .list-product-no-img i {
        font-size: 16px;
      }
    }
  }
}

@media (max-width: 768px) {
  .kkday-product-management {
    padding: 12px;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch !important;
    gap: 16px;

    .page-title {
      text-align: center;
    }

    .header-actions {
      display: flex;
      justify-content: center;
    }
  }

  .product-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    padding: 16px;
  }

  .filter-form {
    .el-form-item {
      display: block !important;
      margin-right: 0;
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .card-header {
    flex-direction: column;
    align-items: stretch !important;
    gap: 16px;

    .header-tools {
      justify-content: center;
    }
  }

  .product-actions {
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;

    .el-button {
      flex: 1;
      min-width: 60px;
    }
  }
}

// Quill编辑器样式
.quill-editor-wrapper {
  position: relative;
  z-index: 100;
  background: #fff;
}

::v-deep .ql-container {
  position: relative;
  z-index: 100;
}

::v-deep .ql-editor {
  min-height: 300px;
  font-size: 14px;
  line-height: 1.6;
  position: relative;
  z-index: 100;
}

::v-deep .ql-toolbar {
  border-radius: 4px 4px 0 0;
  position: relative;
  z-index: 100;
}

// 图片上传样式
.image-uploader {
  position: relative;
  z-index: 1;

  ::v-deep .el-upload {
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    width: 150px;
    height: 150px;
    z-index: 1;

    &:hover {
      border-color: #409eff;
    }
  }
}

.product-image-upload {
  width: 150px;
  height: 150px;
  display: block;
  object-fit: cover;
}

.image-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 150px;
  height: 150px;
  line-height: 150px;
  text-align: center;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}

// 媒体文件上传样式
.media-upload-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: flex-start;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  width: 100%;
}

.video-grid {
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
}

.media-item {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e4e7ed;

  .media-preview {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-preview {
    object-fit: cover;
  }

  .media-actions {
    position: absolute;
    top: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    padding: 4px 6px;
    cursor: pointer;
    border-radius: 0 0 0 4px;
    z-index: 10;

    &:hover {
      background: rgba(0, 0, 0, 0.7);
    }
  }

  .media-label {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 2px 4px;
    font-size: 11px;
    text-align: center;
    z-index: 10;
  }
}

.media-uploader {
  ::v-deep .el-upload {
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    width: 120px;
    height: 120px;

    &:hover {
      border-color: #409eff;
    }
  }

  &.video-uploader {
    ::v-deep .el-upload {
      width: 160px;
      height: 120px;
    }
  }
}

.upload-placeholder {
  width: 120px;
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8c939d;

  i {
    font-size: 24px;
    margin-bottom: 8px;
  }

  span {
    font-size: 12px;
    text-align: center;
    line-height: 1.2;
  }
}

// 响应式设计调整
@media (max-width: 768px) {
  .media-upload-container {
    flex-direction: column;
  }

  .image-uploader {
    ::v-deep .el-upload {
      width: 100px;
      height: 100px;
    }
  }

  .product-image-upload {
    width: 100px;
    height: 100px;
  }

  .image-uploader-icon {
    width: 100px;
    height: 100px;
    line-height: 100px;
    font-size: 20px;
  }

  .media-item {
    width: 80px;
    height: 80px;
  }

  .media-uploader {
    ::v-deep .el-upload {
      width: 80px;
      height: 80px;
    }

    &.video-uploader {
      ::v-deep .el-upload {
        width: 100px;
        height: 80px;
      }
    }
  }

  .upload-placeholder {
    width: 80px;
    height: 80px;

    i {
      font-size: 16px;
      margin-bottom: 4px;
    }

    span {
      font-size: 10px;
    }
  }

  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  }

  .video-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
}

// 套餐编辑对话框
.package-edit-dialog {
  ::v-deep .el-dialog {
    border-radius: 8px;
  }

  ::v-deep .el-dialog__body {
    padding: 0 20px 20px;
  }
}

.form-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 20px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #409eff;
  display: inline-block;
}

.dialog-footer {
  text-align: right;

  .el-button {
    margin-left: 12px;
  }
}
</style>